import { Injectable, OnDestroy } from '@angular/core';
import { MessageService } from 'primeng/api';

export type ToastSeverity = 'success' | 'error' | 'info' | 'warn';

interface QueuedToast {
  message: string;
  severity: ToastSeverity;
  duration: number;
  sticky: boolean;
}

const MIN_DISPLAY_MS = 1000;

@Injectable()
export class PortalToastService implements OnDestroy {
  private queue: QueuedToast[] = [];
  private isProcessing = false;
  private timerId: ReturnType<typeof setTimeout> | null = null;

  constructor(private messageService: MessageService) {}

  ngOnDestroy(): void {
    this.clearTimer();
  }

  success(message: string, duration: number = 5000): void {
    this.enqueue(message, 'success', duration);
  }

  error(message: string, duration: number = 10000, sticky = false): void {
    this.enqueue(message, 'error', duration, sticky);
  }

  info(message: string, duration: number = 5000): void {
    this.enqueue(message, 'info', duration);
  }

  warn(message: string, duration: number = 5000): void {
    this.enqueue(message, 'warn', duration);
  }

  dismiss(): void {
    this.clearTimer();
    this.queue = [];
    this.isProcessing = false;
    this.messageService.clear();
  }

  private enqueue(message: string, severity: ToastSeverity, duration: number, sticky = false): void {
    this.queue.push({ message, severity, duration, sticky });
    if (!this.isProcessing) {
      this.processNext();
    }
  }

  private processNext(): void {
    if (this.queue.length === 0) {
      this.isProcessing = false;
      return;
    }

    this.isProcessing = true;
    const item = this.queue.shift()!;

    this.messageService.clear();
    this.messageService.add({
      severity: item.severity,
      detail: item.message,
      life: item.duration,
      sticky: item.sticky,
    });

    this.timerId = setTimeout(() => {
      this.timerId = null;
      this.processNext();
    }, MIN_DISPLAY_MS);
  }

  private clearTimer(): void {
    if (this.timerId !== null) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }
}
